import React from "react";
import { formatINR } from "../lib/format";
import { Loader2 } from "lucide-react";

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 dark:text-slate-100">{title}</h1>
        {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

const ACCENTS = {
  emerald: "border-l-emerald-500", blue: "border-l-blue-500", amber: "border-l-amber-500",
  rose: "border-l-rose-500", violet: "border-l-violet-500", teal: "border-l-teal-500",
  slate: "border-l-slate-400", indigo: "border-l-indigo-500",
};
const VALCOLOR = {
  emerald: "text-emerald-600 dark:text-emerald-400", blue: "text-blue-600 dark:text-blue-400",
  amber: "text-amber-600 dark:text-amber-400", rose: "text-rose-600 dark:text-rose-400",
  violet: "text-violet-600 dark:text-violet-400", teal: "text-teal-600 dark:text-teal-400",
  slate: "text-slate-800 dark:text-slate-100", indigo: "text-indigo-600 dark:text-indigo-400",
};

export function StatCard({ label, value, icon: Icon, accent = "slate", sub, onClick, isCurrency = true, testid }) {
  return (
    <div onClick={onClick} data-testid={testid}
      className={`bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 border-l-4 ${ACCENTS[accent]} rounded-xl p-4 shadow-sm hover:shadow-md transition-all ${onClick ? "cursor-pointer" : ""}`}>
      <div className="flex items-center justify-between text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1.5">
        <span className="truncate pr-2">{label}</span>
        {Icon && <Icon className={`h-4 w-4 shrink-0 ${VALCOLOR[accent]}`} />}
      </div>
      <div className={`font-mono text-xl font-bold tracking-tight tnum ${VALCOLOR[accent]}`}>
        {isCurrency ? formatINR(value) : value}
      </div>
      {sub && <div className="text-[11px] text-slate-400 mt-1">{sub}</div>}
    </div>
  );
}

export function Loading({ label = "Loading..." }) {
  return <div className="flex items-center justify-center gap-2 py-16 text-slate-400 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> {label}</div>;
}

export function EmptyState({ title = "No records found", sub, action }) {
  return (
    <div className="text-center py-14 text-slate-400">
      <p className="font-medium text-slate-500">{title}</p>
      {sub && <p className="text-sm mt-1">{sub}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Badge({ children, cls = "bg-slate-100 text-slate-700 border-slate-200" }) {
  return <span className={`inline-flex items-center text-[11px] font-semibold px-2.5 py-0.5 rounded-full border ${cls}`}>{children}</span>;
}

export function TableWrap({ children }) {
  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden shadow-sm">
      <div className="overflow-x-auto"><table className="w-full">{children}</table></div>
    </div>
  );
}
export const Th = ({ children, className = "" }) => <th className={`bg-slate-50 dark:bg-slate-800/60 text-slate-600 dark:text-slate-300 text-[11px] font-bold uppercase tracking-wider px-4 py-3 text-left border-b border-slate-200 dark:border-slate-800 whitespace-nowrap ${className}`}>{children}</th>;
export const Td = ({ children, className = "" }) => <td className={`px-4 py-2.5 text-xs text-slate-700 dark:text-slate-300 border-b border-slate-100 dark:border-slate-800/60 whitespace-nowrap ${className}`}>{children}</td>;

export function exportCSV(filename, rows) {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const csv = [headers.join(","), ...rows.map((r) => headers.map((h) => `"${String(r[h] ?? "").replace(/"/g, '""')}"`).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}
